import * as React from "react"
import { Button, Col, Container, Form, Row } from "react-bootstrap"
import { FaMinus, FaPlus } from "react-icons/fa"
import { toast } from "react-toastify"
import { APIError } from "../../api/ObservableFromFetch"
import AuthFormProcessObservable from "../../api/OrderResults/AuthForm"
import { GetOrderRequest } from "../../api/OrderRequests/SSOOrderRequest"
import { EmailRegexp, PhoneRegexp, StorageKey } from "../../config/Constants"
import { GetSSOToken } from "../../storage/AuthToken"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import ProcessingModal from "../ProcessingModal"
import { Subscription } from "rxjs"
import printPdf from "../../utils/printPdf"
import ActionButton from "../ActionButton"
import PhoneNumberMaskedInput from "../FormattedInputs/PhoneNumberMaskedInput"

interface GetAuthFormPageState {
  isLoading: boolean
  ssoToken: string
  spinnerMessage: string
  callbackLink?: string
  formFoxTestAN: number
  emailItems: string[]
  mobileItems: string[]
  sendingEmail: boolean
  sendingText: boolean
  printing: boolean
}
export default class GetAuthFormPage extends React.PureComponent<{}, GetAuthFormPageState> {
  state: GetAuthFormPageState = {
    isLoading: true,
    ssoToken: GetSSOToken(),
    spinnerMessage: "Data is loading...",
    callbackLink: localStorage.getItem(StorageKey.SSOCallbackLink) || undefined,
    formFoxTestAN: 0,
    emailItems: [""],
    mobileItems: [""],
    sendingEmail: false,
    sendingText: false,
    printing: false
  }
  requestSubscription?: Subscription
  actionSubscription?: Subscription

  componentWillUnmount() {
    this.requestSubscription && this.requestSubscription.unsubscribe()
    this.actionSubscription && this.actionSubscription.unsubscribe()
  }
  componentDidMount() {
    this.loadOrderRequest()
  }

  loadOrderRequest = () => {
    this.setState({isLoading:true, spinnerMessage: "Data is loading..."})
    this.requestSubscription && this.requestSubscription.unsubscribe()
    this.requestSubscription = GetOrderRequest(this.state.ssoToken).subscribe(
      data => {
        this.setState({
          formFoxTestAN: data?.FormFoxTestAN || 0,
          isLoading:false,
          spinnerMessage:"",
        })
      },
      error => {
        this.handleAPIError(error, { isLoading: false, spinnerMessage: "" })
      }
    )
  }

  handlePrintClick = () => {
    if (this.state.formFoxTestAN <= 0) {
      toast.error("Authorization number was not found.")
      return
    }

    this.actionSubscription && this.actionSubscription.unsubscribe()
    const params = {
      RequestToken: this.state.ssoToken,
      FormFoxTestAN: this.state.formFoxTestAN,
      PersonID: 0,
      VendorID: 0,
      SendToEmail: false,
      SendText: false,
      EmailAddress: []
    }
    this.setState({ printing: true, spinnerMessage: "Printing..." })
    this.actionSubscription = AuthFormProcessObservable(params).subscribe(
      data => {
        printPdf(data)
      },
      error => this.handleAPIError(error, { printing: false, spinnerMessage: "" }),
      () => this.setState({ printing: false, spinnerMessage: "" })
    )
  }

  addEmail = (index: number, email: string = "") => {
    if (email !== this.state.emailItems[index]) {
      this.setState({ emailItems: [...this.state.emailItems, email] })
    }
  }

  removeEmail = (_email: string, index: number) => {
    const array = [...this.state.emailItems]
    if (index > -1) {
      array.splice(index, 1)
      this.setState({ emailItems: array })
    }
  }

  changeEmail = (email: string, index: number) => {
    const array = [...this.state.emailItems]
    if (index > -1) {
      array[index] = email
      this.setState({ emailItems: array })
    }
  }

  isEmailValid = (value: string): boolean => {
    if (value.length === 0) {
      return true
    }
    return EmailRegexp.test(value)
  }

  canAddEmail = (email: string): boolean => email.length > 0 && this.isEmailValid(email)

  sendEmail = () => {
    if (this.state.formFoxTestAN <= 0) {
      toast.error("Authorization number was not found.")
      return
    }
    const filteredEmails = this.state.emailItems.filter((email) => email.trim().length > 0)
    if (filteredEmails.length <= 0) {
      toast.error("Please enter email address.")
      return
    }
    if (Array.from(new Set(filteredEmails.map(value => value.toLowerCase()))).length !== filteredEmails.length) {
      toast.error("Duplicate email address is present in the list.")
      return
    }

    const params = {
      RequestToken: this.state.ssoToken,
      EmailAddress: filteredEmails,
      FormFoxTestAN: this.state.formFoxTestAN,
      PersonID: 0,
      VendorID: 0,
      SendToEmail: true,
      SendText: false
    }
    this.actionSubscription && this.actionSubscription.unsubscribe()
    this.setState({ sendingEmail: true, spinnerMessage: "Sending email..." })
    this.actionSubscription = AuthFormProcessObservable(params).subscribe(
      data => toast.success(data),
      error => this.handleAPIError(error, { sendingEmail: false, spinnerMessage: "" }),
      () => this.setState({ sendingEmail: false, spinnerMessage: "" })
    )
  }

  addMobile = (index: number, phone: string = "") => {
    if (phone !== this.state.mobileItems[index]) {
      this.setState({ mobileItems: [...this.state.mobileItems, phone] })
    }
  }

  removeMobile = (_phone: string, index: number) => {
    const array = [...this.state.mobileItems]
    if (index > -1) {
      array.splice(index, 1)
      this.setState({ mobileItems: array })
    }
  }

  changeMobile = (phone: string, index: number) => {
    const array = [...this.state.mobileItems]
    if (index > -1) {
      array[index] = phone
      this.setState({ mobileItems: array })
    }
  }

  isPhoneValid = (value: string): boolean => value.length === 0 || PhoneRegexp.test(value)
  canAddMobile = (mobile: string): boolean => mobile.length > 0 && this.isPhoneValid(mobile)

  sendText = () => {
    if (this.state.formFoxTestAN <= 0) {
      toast.error("Authorization number was not found.")
      return
    }
    const filteredMobileItems = this.state.mobileItems.filter((mobile) => mobile.trim().length > 0)
    if (filteredMobileItems.length <= 0) {
      toast.error("Please enter mobile number.")
      return
    }
    if (Array.from(new Set(filteredMobileItems.map(value => value.toLowerCase()))).length !== filteredMobileItems.length) {
      toast.error("Duplicate mobile number is present in the list.")
      return
    }

    const params = {
      RequestToken: this.state.ssoToken,
      EmailAddress: filteredMobileItems,
      FormFoxTestAN: this.state.formFoxTestAN,
      PersonID: 0,
      VendorID: 0,
      SendToEmail: false,
      SendText: true
    }
    this.actionSubscription && this.actionSubscription.unsubscribe()
    this.setState({ sendingText: true, spinnerMessage: "Sending text..." })
    this.actionSubscription = AuthFormProcessObservable(params).subscribe(
      () => toast.success("Text sent successfully"),
      error => this.handleAPIError(error, { sendingText: false, spinnerMessage: "" }),
      () => this.setState({ sendingText: false, spinnerMessage: "" })
    )
  }

  handleFinish = () => {
    const callbackLink = this.state.callbackLink
    if (!callbackLink) {
      toast.error("Callback link is not available.")
      return
    }
    localStorage.removeItem(StorageKey.SSOCallbackLink)
    window.location.replace(callbackLink)
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  render() {
    const { isLoading, callbackLink, emailItems, mobileItems, sendingEmail, sendingText, printing, formFoxTestAN } = this.state
    return <AppPageWrapper showNav={true}>
      {(isLoading || sendingEmail || sendingText || printing) && <ProcessingModal message={this.state.spinnerMessage} />}
      {!isLoading && <Container className="pt-3">
        <Row>
          <Col>
            <h4 className="primary text-uppercase">
              <span>AUTHORIZATION # </span>
              <span className="authorizationNumber">({formFoxTestAN || "-"})</span>
            </h4>
          </Col>
        </Row>
        {formFoxTestAN <= 0 && <Row className="pb-2">
          <Col>Unable to load authorization details for this token. Print/Email/Text actions are unavailable.</Col>
        </Row>}
        <Row className="pb-2">
          <Col xs={3} lg={2}>Print form:</Col>
          <Col xs={9} lg={10} className="d-flex">
            <Button size="sm" className="fixed-w10" onClick={this.handlePrintClick} disabled={formFoxTestAN <= 0 || sendingEmail || sendingText || printing}>Print PDF</Button>
          </Col>
        </Row>
        {emailItems.map((email, index) => <Row className="pt-2" key={`email-${index}`}>
          <Col xs={12} sm={3} md={3} lg={2}>
            { index === 0 && "Email form:" }
          </Col>
          <Col xs={9} sm={5} md={4}>
            <Form.Control size="sm" name="email_address" maxLength={40} type="text" value={email} onChange={(e) => this.changeEmail(e.target.value, index)} readOnly={emailItems.length !== index + 1} isInvalid={!this.isEmailValid(email)} />
          </Col>
          <Col xs={3} sm={4} md={5}>
            { emailItems.length === index + 1 ? <Button size="sm" variant="primary" className="btn-sm-icon" onClick={() => this.addEmail(index)} disabled={!this.canAddEmail(email)}><FaPlus /></Button> : <Button size="sm" variant="primary" className="btn-sm-icon" onClick={() => this.removeEmail(email, index)}><FaMinus /></Button> }
          </Col>
        </Row>)}
        <Row className="py-2">
          <Col xs={12} sm={3} md={3} lg={2}></Col>
          <Col xs={12} sm={3}><ActionButton size="sm" className="fixed-w10" onClick={this.sendEmail} disabled={formFoxTestAN <= 0 || !emailItems.every((email) => this.isEmailValid(email)) || sendingEmail || sendingText || printing} spin={sendingEmail} disableOnSpin>Send Email</ActionButton></Col>
        </Row>
        {mobileItems.map((mobile, index) => <Row className="pt-2" key={`mobile-${index}`}>
          <Col xs={12} sm={3} md={3} lg={2}>
            { index === 0 && "Text form:" }
          </Col>
          <Col xs={9} sm={5} md={4}>
            <PhoneNumberMaskedInput size="sm" value={mobile} onChange={(value: string) => this.changeMobile(value, index)} readOnly={mobileItems.length !== index + 1} isInvalid={!this.isPhoneValid(mobile)} />
          </Col>
          <Col xs={3} sm={4} md={5}>
            { mobileItems.length === index + 1 ? <Button size="sm" variant="primary" className="btn-sm-icon" onClick={() => this.addMobile(index)} disabled={!this.canAddMobile(mobile)}><FaPlus /></Button> : <Button size="sm" variant="primary" className="btn-sm-icon" onClick={() => this.removeMobile(mobile, index)}><FaMinus /></Button> }
          </Col>
        </Row>)}
        <Row className="py-2">
          <Col xs={12} sm={3} md={3} lg={2}></Col>
          <Col xs={12} sm={3}><ActionButton size="sm" className="fixed-w10" onClick={this.sendText} disabled={formFoxTestAN <= 0 || !mobileItems.every((mobile) => this.isPhoneValid(mobile)) || sendingEmail || sendingText || printing} spin={sendingText} disableOnSpin>Send Text</ActionButton></Col>
        </Row>
        {callbackLink && <Row className="pt-3">
          <Col>
            <Button size="sm" onClick={this.handleFinish}>FINISH</Button>
          </Col>
        </Row>}
      </Container>}
    </AppPageWrapper>
  }
}
